import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
  ChevronDown,
  Columns3,
  Download,
  List,
  Loader2,
  Megaphone,
  Plus,
  Search,
  SquareKanban,
  X,
} from 'lucide-react';
import {DetailHeader} from '@internal/components/DetailHeader';
import {Tabs, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {Input} from '@common-web/components/ui/input';
import {Button} from '@common-web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {toast} from '@common-web/hooks/use-toast';
import {createCsv} from '@common-web/lib/createCsv';
import {downloadCsvFile, makeCsvFilename} from '@common-web/lib/downloadCsv';
import {trpc} from '@common-web/lib/trpc';
import {ACTIVE_PHASE, VBA_STATUSES, type VbaStatus} from '@common/groupPipeline/types';
import {
  PipelineFilters,
  type FilterSection,
} from '@internal/pages/group-pipeline/all-groups/PipelineFilters';
import {
  GroupPipelineList,
  PAGE_SIZE_OPTIONS,
} from '@internal/pages/group-pipeline/all-groups/GroupPipelineList';
import {GroupPipelineBoard} from '@internal/pages/group-pipeline/all-groups/GroupPipelineBoard';
import {CustomizeView} from '@internal/pages/group-pipeline/all-groups/CustomizeView';
import {PipelineCountRow} from '@internal/pages/group-pipeline/all-groups/PipelineCountRow';
import {useGroupPipelineView} from '@internal/pages/group-pipeline/all-groups/useGroupPipelineView';
import {groupsCsvRecords} from '@internal/pages/group-pipeline/all-groups/groupsCsv';
import {comingSoon} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {StartPipelineDialog} from '@internal/pages/group-pipeline/components/StartPipelineDialog';
import {StartFromAdmin123Dialog} from '@internal/pages/group-pipeline/components/StartFromAdmin123Dialog';
import {VBA_STATUS_DISPLAY} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';
import {
  STATUS_DISPLAY,
  type PipelineStatus,
} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';

type PipelineView = 'list' | 'board';

const parseView = (value: string | null): PipelineView => (value === 'board' ? 'board' : 'list');
const parseList = (value: string | null) => value?.split(',').filter(Boolean) ?? [];
const parsePositiveInt = (value: string | null, fallback: number) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const FILTER_PARAMS = ['tenant', 'phase', 'step', 'status', 'vba'] as const;

const STATUS_OPTIONS = Object.entries(STATUS_DISPLAY).map(([value, {label}]) => ({
  value: value as NonNullable<PipelineStatus>,
  label,
}));
const isStatus = (value: string): value is NonNullable<PipelineStatus> =>
  STATUS_OPTIONS.some((option) => option.value === value);

const VBA_STATUS_OPTIONS = VBA_STATUSES.map((value) => ({
  value,
  label: VBA_STATUS_DISPLAY[value].label,
}));
const isVbaStatus = (value: string): value is VbaStatus =>
  VBA_STATUSES.some((status) => status === value);

export function GroupPipelinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get('view'));
  const search = searchParams.get('q') ?? '';
  const tenantIds = parseList(searchParams.get('tenant'));
  const phases = parseList(searchParams.get('phase'));
  const currentStepKeys = parseList(searchParams.get('step'));
  const statuses = parseList(searchParams.get('status')).filter(isStatus);
  const vbaStatuses = parseList(searchParams.get('vba')).filter(isVbaStatus);
  const page = parsePositiveInt(searchParams.get('page'), 1);
  const requestedPageSize = parsePositiveInt(searchParams.get('pageSize'), 25);
  // Only the offered sizes; the API rejects anything above 100.
  const pageSize = PAGE_SIZE_OPTIONS.includes(requestedPageSize) ? requestedPageSize : 25;

  const [searchInput, setSearchInput] = useState(search);
  const [startOpen, setStartOpen] = useState(false);
  const [admin123Open, setAdmin123Open] = useState(false);

  const updateParams = (changes: Record<string, string | null>, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) params.set(key, value);
      else params.delete(key);
    }
    if (resetPage) params.delete('page');
    setSearchParams(params, {replace: true});
  };

  // Debounce the search box into `q` so each keystroke doesn't hit the API or reset page.
  useEffect(() => {
    const trimmed = searchInput.trim();
    const t = setTimeout(
      () =>
        setSearchParams(
          (prev) => {
            if ((prev.get('q') ?? '') === trimmed) return prev;
            const params = new URLSearchParams(prev);
            if (trimmed) params.set('q', trimmed);
            else params.delete('q');
            params.delete('page');
            return params;
          },
          {replace: true},
        ),
      300,
    );
    return () => clearTimeout(t);
  }, [searchInput, setSearchParams]);

  const utils = trpc.useUtils();
  const [exporting, setExporting] = useState(false);
  const {data: options} = trpc.internal.groupPipeline.filterOptions.useQuery();
  const savedView = useGroupPipelineView();

  const filters = {
    search: search || undefined,
    tenantIds: tenantIds.length ? tenantIds : undefined,
    currentStepKeys: currentStepKeys.length ? currentStepKeys : undefined,
    statuses: statuses.length ? statuses : undefined,
    vbaStatuses: vbaStatuses.length ? vbaStatuses : undefined,
  };
  const list = trpc.internal.groupPipeline.list.useQuery(
    {...filters, phases: phases.length ? phases : undefined, page, pageSize},
    {enabled: view === 'list', placeholderData: (previous) => previous},
  );
  const board = trpc.internal.groupPipeline.board.useQuery(
    {
      ...filters,
      phases: phases.length ? phases : undefined,
      columnKeys: savedView.view.boardColumns,
    },
    {
      enabled: view === 'board' && !savedView.isLoading,
      placeholderData: (previous) => previous,
    },
  );

  // Exports what's on screen: every page of the list, or the groups the board shows.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const boardStatuses = (
        statuses.length ? statuses : (['ON_TRACK', 'OVERDUE'] as const)
      ).filter((st) => st === 'ON_TRACK' || st === 'OVERDUE');
      const rows = await utils.internal.groupPipeline.exportRows.fetch({
        ...filters,
        phases: phases.length ? phases : undefined,
        statuses: view === 'board' ? boardStatuses : filters.statuses,
      });
      if (!rows.length) {
        toast({title: 'No groups to export'});
        return;
      }
      const {headers, records} = groupsCsvRecords(rows, savedView.view.listColumns);
      downloadCsvFile(createCsv(records, headers), makeCsvFilename('group-pipeline'));
    } catch {
      toast({title: 'Could not export', variant: 'destructive'});
    } finally {
      setExporting(false);
    }
  };

  const sections: FilterSection[] = [
    {
      key: 'tenant',
      label: 'Tenant',
      options: options?.tenants.map((t) => ({value: t.id, label: t.name})) ?? [],
      selected: tenantIds,
    },
    {
      key: 'phase',
      label: 'Phase',
      options: options?.phases.map((p) => ({value: p, label: p})) ?? [],
      selected: phases,
      // Live groups never appear on the board, so Active can't filter it.
      disabled:
        view === 'board'
          ? {
              values: [ACTIVE_PHASE],
              reason:
                "Active client groups aren't shown on the board, so this only applies in List.",
            }
          : undefined,
    },
    {
      key: 'step',
      label: 'Current step',
      options: options?.steps.map((s) => ({value: s.key, label: s.label})) ?? [],
      selected: currentStepKeys,
    },
    {
      key: 'status',
      label: 'Group status',
      options: STATUS_OPTIONS,
      selected: statuses,
      // The board only shows groups that are moving, so hold/NMF can't filter it.
      disabled:
        view === 'board'
          ? {
              values: ['ON_HOLD', 'NOT_MOVING_FORWARD'],
              reason:
                "On hold and not-moving-forward groups aren't shown on the board, so this only applies in List.",
            }
          : undefined,
    },
    {
      key: 'vba',
      label: 'VBA status',
      options: VBA_STATUS_OPTIONS,
      selected: vbaStatuses,
    },
  ];

  const count = view === 'list' ? list.data?.total : board.data?.total;
  const customizeView = (
    <CustomizeView
      activeView={view}
      view={savedView.view}
      steps={options?.steps ?? []}
      isCustomized={savedView.isCustomized}
      onChange={savedView.update}
      onReset={savedView.reset}
    />
  );
  const emptyColumnCount = board.data?.columns.filter((c) => c.total === 0).length ?? 0;

  return (
    <div className='space-y-4 py-6'>
      {/* Header and list stay in the page gutter; the board spans the full width. */}
      <div className='mx-auto w-full max-w-7xl space-y-4 px-6'>
        <DetailHeader
          icon={<SquareKanban className='h-5 w-5' />}
          title='Group Pipeline'
          subtitle='Every group from onboarding to active client.'
          actions={
            <div className='flex items-center gap-2'>
              <Button
                variant='outline'
                className='relative bg-white'
                title={NOT_BUILT}
                onClick={comingSoon("Today's outreach")}
              >
                <Megaphone className='mr-1.5 h-4 w-4' />
                Today&apos;s outreach
                <PreviewDot />
              </Button>
              <Button
                variant='outline'
                className='bg-white'
                disabled={exporting}
                // Not returning the promise keeps Button's own loading state (an invisible
                // spinner) out of the way.
                onClick={() => void exportCsv()}
              >
                {exporting ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <Download className='mr-1.5 h-4 w-4' />
                )}
                Export
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className='mr-1.5 h-4 w-4' />
                    Add group
                    <ChevronDown className='ml-1 h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-72'>
                  <DropdownMenuItem
                    className='flex-col items-start gap-0.5'
                    onSelect={() => setAdmin123Open(true)}
                  >
                    <span className='font-medium'>From Admin123</span>
                    <span className='text-xs text-muted-foreground'>
                      Add the group before it&apos;s in VBA. It carries over once saved in VBA.
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className='flex-col items-start gap-0.5'
                    onSelect={() => setStartOpen(true)}
                  >
                    <span className='font-medium'>From VBA</span>
                    <span className='text-xs text-muted-foreground'>
                      The group already exists in VBA. Add it to the pipeline.
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className='flex-col items-start gap-0.5'
                    onSelect={comingSoon('Build in VBA')}
                    title={NOT_BUILT}
                  >
                    <span className='flex w-full items-center gap-2 font-medium'>
                      Build in VBA
                      <PreviewDot inline />
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      Create the group in VBA first, then add it to the pipeline.
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        />

        <div className='flex flex-wrap items-center gap-3'>
          <Tabs value={view} onValueChange={(next) => updateParams({view: next}, false)} noHash>
            <TabsList>
              <TabsTrigger value='list' noHash className='gap-2'>
                <List className='h-4 w-4' />
                List
              </TabsTrigger>
              <TabsTrigger value='board' noHash className='gap-2'>
                <Columns3 className='h-4 w-4' />
                Board
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className='relative w-full max-w-xs'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder='Search name or ID'
              className='pl-9 pr-9'
            />
            {searchInput && (
              <button
                type='button'
                onClick={() => setSearchInput('')}
                aria-label='Clear search'
                className='absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>

          <PipelineFilters
            sections={sections}
            onChange={(key, selected) => updateParams({[key]: selected.join(',') || null})}
            onClearAll={() =>
              updateParams(Object.fromEntries(FILTER_PARAMS.map((key) => [key, null])))
            }
          />
        </div>

        {view === 'list' ? (
          <PipelineCountRow count={count} actions={customizeView} />
        ) : (
          <PipelineCountRow
            actions={customizeView}
            count={count}
            hiddenActiveCount={board.data?.hiddenActiveCount}
            emptyColumnCount={emptyColumnCount}
            hideEmptyColumns={savedView.view.hideEmptyColumns}
            onHideEmptyColumnsChange={(hideEmptyColumns) => savedView.update({hideEmptyColumns})}
          />
        )}

        {view === 'list' && (
          <GroupPipelineList
            columns={savedView.view.listColumns}
            data={list.data}
            isLoading={list.isLoading}
            isFetching={list.isFetching}
            page={page}
            pageSize={pageSize}
            onPageChange={(next) => updateParams({page: String(next)}, false)}
            onPageSizeChange={(next) => updateParams({pageSize: String(next)})}
          />
        )}
      </div>

      {view === 'board' && (
        <div className='px-6'>
          <GroupPipelineBoard
            data={board.data}
            filters={{
              ...filters,
              phases: phases.length ? phases : undefined,
              columnKeys: savedView.view.boardColumns,
            }}
            hideEmptyColumns={savedView.view.hideEmptyColumns}
            isLoading={board.isLoading}
            isFetching={board.isFetching}
          />
        </div>
      )}
      <StartPipelineDialog open={startOpen} onOpenChange={setStartOpen} />
      <StartFromAdmin123Dialog open={admin123Open} onOpenChange={setAdmin123Open} />
    </div>
  );
}
